import {
  CriterionCoachingPriority,
  findNodeWithPath,
  isPlaybookCriterionNode,
  isPlaybookSectionNode
} from "../entities/playbook.js";
import {
  addCriteria,
  addSection,
  findCriteria,
  findSection,
  updateCriteria,
  updateSection
} from "./playbookTreeOperations.js";
function applyNodePublish(live, draft, nodeId) {
  const draftMatch = findNodeWithPath(draft, nodeId);
  if (!draftMatch) {
    return void 0;
  }
  const { node, sectionsStack } = draftMatch;
  if (isPlaybookSectionNode(node)) {
    const liveSection = findSection(live, nodeId);
    if (liveSection) {
      return {
        tree: updateSection(live, { ...node, nodes: liveSection.nodes }),
        publishedNodeIds: [nodeId]
      };
    }
  } else if (findCriteria(live, nodeId)) {
    return { tree: updateCriteria(live, node), publishedNodeIds: [nodeId] };
  }
  const { newAncestors, liveAncestor } = splitAncestors(live, sectionsStack);
  let tree = live;
  let parent = liveAncestor;
  for (const ancestor of newAncestors) {
    tree = addSection(
      tree,
      { ...ancestor, nodes: [] },
      parent?.id,
      draftRelativeIndex(tree, parent, draft, ancestor.id)
    );
    parent = ancestor;
  }
  const index = draftRelativeIndex(tree, parent, draft, nodeId);
  if (isPlaybookSectionNode(node)) {
    tree = addSection(tree, { ...node, nodes: [] }, parent?.id, index);
  } else if (parent) {
    tree = addCriteria(tree, node, parent.id, index);
  } else {
    throw new Error(`Criterion ${nodeId} has no section in the draft`);
  }
  return {
    tree,
    publishedNodeIds: [...newAncestors.map((section) => section.id), nodeId]
  };
}
function draftRelativeIndex(tree, parent, draft, nodeId) {
  const liveSiblings = parent === void 0 ? tree.nodes : findSection(tree, parent.id)?.nodes;
  if (!liveSiblings) {
    throw new Error(`Section ${parent?.id} is not in the tree`);
  }
  const draftSiblings = parent === void 0 ? draft.nodes : parent.nodes;
  const draftPosition = draftSiblings.findIndex((node) => node.id === nodeId);
  const precedingLiveIndices = draftSiblings.slice(0, draftPosition).map((sibling) => liveSiblings.findIndex((node) => node.id === sibling.id)).filter((index) => index !== -1);
  return precedingLiveIndices.length === 0 ? 0 : Math.max(...precedingLiveIndices) + 1;
}
function splitAncestors(live, sectionsStack) {
  const newAncestors = [];
  let liveAncestor;
  for (const section of sectionsStack.toReversed()) {
    if (findSection(live, section.id)) {
      liveAncestor = section;
      break;
    }
    newAncestors.unshift(section);
  }
  return { newAncestors, liveAncestor };
}
function getPublishNodeDisclosure(live, draft, nodeId) {
  const draftMatch = findNodeWithPath(draft, nodeId);
  if (!draftMatch) {
    return void 0;
  }
  const { node, sectionsStack } = draftMatch;
  const liveNode = isPlaybookSectionNode(node) ? findSection(live, nodeId) : findCriteria(live, nodeId);
  return {
    changedFields: liveNode ? changedNodeFields(liveNode, node) : [],
    isNew: !liveNode,
    newAncestorSectionCount: liveNode ? 0 : splitAncestors(live, sectionsStack).newAncestors.length,
    globalOnlyChanges: collectGlobalOnlyChanges(live, draft)
  };
}
function changedNodeFields(liveNode, draftNode) {
  const changed = [];
  if (liveNode.name !== draftNode.name) {
    changed.push("name");
  }
  if (liveNode.description !== draftNode.description) {
    changed.push("description");
  }
  if (isPlaybookCriterionNode(liveNode) && isPlaybookCriterionNode(draftNode)) {
    if (liveNode.grading_context !== draftNode.grading_context) {
      changed.push("grading_context");
    }
    if (liveNode.grading_value_type !== draftNode.grading_value_type) {
      changed.push("grading_value_type");
    }
    if ((liveNode.coaching_priority ?? CriterionCoachingPriority.Normal) !== (draftNode.coaching_priority ?? CriterionCoachingPriority.Normal)) {
      changed.push("coaching_priority");
    }
  }
  return changed;
}
function collectPlacements(tree) {
  const parentIds = /* @__PURE__ */ new Map();
  const childIds = /* @__PURE__ */ new Map();
  function visit(nodes, parentId) {
    childIds.set(
      parentId,
      nodes.map((node) => node.id)
    );
    for (const node of nodes) {
      parentIds.set(node.id, parentId);
      if (isPlaybookSectionNode(node)) {
        visit(node.nodes, node.id);
      }
    }
  }
  visit(tree.nodes, null);
  return { parentIds, childIds };
}
function collectGlobalOnlyChanges(live, draft) {
  const liveTree = collectPlacements(live);
  const draftTree = collectPlacements(draft);
  const hasMoves = [...liveTree.parentIds].some(
    ([id, parentId]) => draftTree.parentIds.has(id) && draftTree.parentIds.get(id) !== parentId
  );
  const hasDeletions = [...liveTree.parentIds.keys()].some(
    (id) => !draftTree.parentIds.has(id)
  );
  const hasReorders = [...liveTree.childIds].some(([parentId, liveIds]) => {
    const draftIds = draftTree.childIds.get(parentId) ?? [];
    const sharedInLiveOrder = liveIds.filter(
      (id) => draftTree.parentIds.get(id) === parentId
    );
    const sharedInDraftOrder = draftIds.filter(
      (id) => liveTree.parentIds.get(id) === parentId
    );
    return sharedInLiveOrder.join(",") !== sharedInDraftOrder.join(",");
  });
  return [
    ...hasMoves ? ["moves"] : [],
    ...hasReorders ? ["reorders"] : [],
    ...hasDeletions ? ["deletions"] : []
  ];
}
export {
  applyNodePublish,
  getPublishNodeDisclosure
};
//# sourceMappingURL=playbookNodePublish.js.map
